import React from 'react';
import { Container, Typography, Box, Grid, Paper } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Context
import { useI18n } from '../context/I18nContext';

const PageContainer = styled(Container)`
  padding-top: 2rem;
  padding-bottom: 4rem;
`;

const AboutImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  object-fit: cover;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
  
  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const AboutCard = styled(Paper)`
  padding: 2.5rem 2rem;
  height: 100%;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  min-height: 220px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: var(--primary-color);
  }
`;

const AboutPage: React.FC = () => {
  const { t } = useI18n();
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageContainer maxWidth="lg">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h2" component="h1" align="center" gutterBottom color="primary">
            {t('about_title')}
          </Typography>
          
          <Box sx={{ mt: 6, mb: 8 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
              <Box sx={{ width: { xs: '100%', md: '50%' }, px: 2 }}>
                <motion.div
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '350px',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        backgroundImage: `url('/assets/images/illustrations/turoad-concept.jpg')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                      aria-label="Usuário navegando no aplicativo TURoad"
                    />
                    <Typography variant="caption" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                      Transformando a experiência de turismo com tecnologia e audioguias
                    </Typography>
                  </Box>
                </motion.div>
              </Box>
              
              <Box sx={{ width: { xs: '100%', md: '50%' }, px: 2 }}>
                <motion.div
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Typography variant="h4" gutterBottom color="primary">
                    Nossa Missão
                  </Typography>
                  
                  <Typography variant="body1" paragraph>
                    {t('about_p1')}
                  </Typography>
                  
                  <Typography variant="body1" paragraph>
                    {t('about_p2')}
                  </Typography>
                  
                  <Typography variant="body1" paragraph>
                    {t('about_p3')}
                  </Typography>
                </motion.div>
              </Box>
            </Box>
          </Box>
          
          <Typography variant="h4" align="center" gutterBottom color="primary" sx={{ mt: 6, mb: 4 }}>
            Nossos Valores
          </Typography>
          
          <ValuesGrid>
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              style={{ height: '100%' }}
            >
              <AboutCard elevation={3}>
                <Typography variant="h5" gutterBottom color="primary">
                  Sustentabilidade
                </Typography>
                <Typography variant="body1">
                  Promovemos práticas sustentáveis e eco-friendly em todas as nossas rotas turísticas, 
                  incentivando o respeito ao meio ambiente e às comunidades locais.
                </Typography>
              </AboutCard>
            </motion.div>
            
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              style={{ height: '100%' }}
            >
              <AboutCard elevation={3}>
                <Typography variant="h5" gutterBottom color="primary">
                  Inclusão
                </Typography>
                <Typography variant="body1">
                  Acreditamos que o turismo deve ser acessível a todos. Trabalhamos para criar 
                  experiências inclusivas que conectam pessoas de diferentes origens e habilidades.
                </Typography>
              </AboutCard>
            </motion.div>
            
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              style={{ height: '100%' }}
            >
              <AboutCard elevation={3}>
                <Typography variant="h5" gutterBottom color="primary">
                  Inovação
                </Typography>
                <Typography variant="body1">
                  Utilizamos tecnologia de ponta para criar experiências turísticas inovadoras e 
                  memoráveis, sempre buscando novas formas de conectar pessoas a lugares.
                </Typography>
              </AboutCard>
            </motion.div>
          </ValuesGrid>
          
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <Typography variant="h4" gutterBottom color="primary">
                Nossa Equipe
              </Typography>
              <Typography variant="body1" paragraph>
                Somos uma equipe multidisciplinar apaixonada por turismo, tecnologia e desenvolvimento regional.
                Nosso objetivo é transformar a experiência de visitantes e moradores, proporcionando conhecimento 
                sobre a história, cultura e belezas naturais das cidades brasileiras.
              </Typography>
            </motion.div>
          </Box>
        </motion.div>
      </PageContainer>
    </motion.div>
  );
};

export default AboutPage;
